import type { GraphEditPatch } from './GraphTopology';

type Identified = { id: string };
function changedRecords<T extends Identified>(previous: T[], next: T[]): Set<string> {
  const before = new Map(previous.map((entry) => [entry.id, entry]));
  const changed = new Set<string>();
  for (const entry of next) {
    const old = before.get(entry.id);
    if (
      !old ||
      Object.keys(entry).some(
        (key) => !Object.is(entry[key as keyof T], old[key as keyof T]),
      )
    )
      changed.add(entry.id);
    before.delete(entry.id);
  }
  for (const id of before.keys()) changed.add(id);
  return changed;
}

/** Bridge the existing authored-input observer to explicit graph patches. The
 * journals include structural and endpoint changes that a value comparison
 * alone cannot detect, including same-side connection retargeting. */
export function graphEditPatch<
  T extends { tiles: Identified[]; chipInstances: Identified[] },
>(
  previous: T,
  next: T,
  tiles: ReadonlySet<string>,
  connections: ReadonlySet<string>,
): GraphEditPatch {
  const tileIds = changedRecords(previous.tiles, next.tiles);
  for (const id of tiles) tileIds.add(id);
  for (const id of connections) tileIds.add(id);
  return { tileIds, chipIds: changedRecords(previous.chipInstances, next.chipInstances) };
}
